import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import { memo } from "react";
import { useSelector } from "react-redux";

import CustomInput from "../../components/inputs/CustomInput";
import { USER_ROLES } from "../../utils/constants";
import { findObjectKeyByValue } from "../../utils/helper";
import CustomDialog from "../../components/layouts/common/CustomDialog";
import { CenteredBox } from "../../components/layouts/OneViewBox";
import {
  FOLLOWUP_TYPE,
  FOLLOW_UP_LOG_TYPE,
  HEARING_STATE,
  INCLUSION_STATE,
  INCLUSION_STATE_NAME,
  SPEECH_STATE,
} from "../../utils/beneficiary.constant";
import { MobileDatePicker } from "@mui/x-date-pickers";
import moment from "moment";

const UpdateFollowupStatusUI = ({
  title,
  modalKey,
  isUpdate,
  fields,
  setFields,
  loading,
  onSubmit,
  type,
  params,
}) => {
  const { user } = useSelector((state) => state);
  const initialHearingStage = Number(params?.last_hearing_state ?? 0);
  const initialSpeechStage = Number(params?.last_speech_state ?? 0);

  const answerTypes = () => {
    return {
      [FOLLOW_UP_LOG_TYPE.CALL_NO_ANSWERED]: "Call Not Answered",
      [FOLLOW_UP_LOG_TYPE.CALL_RESCHEDULED]: "Call Rescheduled",
      [FOLLOW_UP_LOG_TYPE.CALL_ANSWERED]: "Call Answered",
    };
  };
  const hearingQuestionLabels = [
    "Is the beneficiary able to wear, charge, and hold the hearing device comfortably on their own?",
    "Has the beneficiary started responding to music and sharing expressions while hearing?",
    "Does the beneficiary respond when called by name and recognize basic words?",
    "Can the beneficiary hear and understand complete sentences?",
  ];
  const speechQuestionLabels = [
    "Has the beneficiary started expressing themselves through facial expressions or spoken their first words?",
    "Does the beneficiary attempt to repeat words they hear from others?",
    "Can the beneficiary say key short words like 'Maa' (Mother) or 'Pappa' (Father)?",
    "Is the beneficiary able to answer simple questions using multiple words?",
  ];

  const getProgressFromAnswers = (answers, progressValues) => {
    if (answers[3]) return progressValues[3];
    if (answers[2]) return progressValues[2];
    if (answers[1]) return progressValues[1];
    if (answers[0]) return progressValues[0];
    return progressValues[0];
  };

  const handleQuestionChange = (
    questionKey,
    questionKeys,
    progressKey,
    progressValues,
    checkedValue,
  ) => {
    setFields((previousFields) => {
      const nextFields = {
        ...previousFields,
        [questionKey]: checkedValue,
      };

      if (!checkedValue) {
        const startIndex = questionKeys.indexOf(questionKey);
        questionKeys.slice(startIndex + 1).forEach((key) => {
          nextFields[key] = false;
        });
      }

      nextFields[progressKey] = getProgressFromAnswers(
        questionKeys.map((key) => nextFields[key]),
        progressValues,
      );

      return nextFields;
    });
  };

  const renderQuestion = (questionKey, label, onChange) => (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <FormLabel component="legend">{label}</FormLabel>
      <RadioGroup
        row
        value={String(fields[questionKey])}
        onChange={(event) => {
          onChange(event.target.value === "true");
        }}
      >
        <FormControlLabel value="true" control={<Radio />} label="Yes" />
        <FormControlLabel value="false" control={<Radio />} label="No" />
      </RadioGroup>
    </FormControl>
  );

  const renderDisabledQuestion = (questionKey, label) => (
    <FormControl component="fieldset" sx={{ width: "100%" }} disabled>
      <FormLabel component="legend">{label}</FormLabel>
      <RadioGroup row value={String(fields[questionKey])}>
        <FormControlLabel value="true" control={<Radio />} label="Yes" />
        <FormControlLabel value="false" control={<Radio />} label="No" />
      </RadioGroup>
    </FormControl>
  );

  return (
    <>
      <CustomDialog
        id={modalKey}
        loading={loading}
        err={fields.err}
        onSubmit={onSubmit}
        title={`${isUpdate ? "Update" : "Create"} ${title}`}
        closeText="Close"
        confirmText={`${isUpdate ? "Update" : "Create"}`}
      >
        {loading ? (
          <CenteredBox>
            <CircularProgress />
          </CenteredBox>
        ) : (
          <>
            <Autocomplete
              disableClearable
              value={fields.status ? answerTypes()[fields.status] : null}
              onChange={(e, changedVal) => {
                setFields({
                  ...fields,
                  status: changedVal ? changedVal._id : null,
                });
              }}
              options={[
                {
                  label: "Call Not Answered",
                  _id: FOLLOW_UP_LOG_TYPE.CALL_NO_ANSWERED,
                },
                {
                  label: "Call Rescheduled",
                  _id: FOLLOW_UP_LOG_TYPE.CALL_RESCHEDULED,
                },
                {
                  label: "Call Answered",
                  _id: FOLLOW_UP_LOG_TYPE.CALL_ANSWERED,
                },
              ]}
              label="Select Status"
              sx={{
                width: "100%",
                display: "flex",
                "*": { display: "flex", justifyContent: "center" },
              }}
              renderInput={(params) => (
                <CustomInput
                  label="Select Status"
                  placeholder="Select Status"
                  {...params}
                />
              )}
            />

            {fields.status === FOLLOW_UP_LOG_TYPE.CALL_ANSWERED ? (
              <>
                {fields.type === FOLLOWUP_TYPE.INCLUSIVE ? (
                  <>
                    <Autocomplete
                      disableClearable
                      value={findObjectKeyByValue(
                        fields.inclusion_state,
                        INCLUSION_STATE,
                      )}
                      onChange={(e, changedVal) => {
                        setFields({
                          ...fields,
                          inclusion_state: changedVal ? changedVal._id : null,
                        });
                      }}
                      options={Object.keys(INCLUSION_STATE).map((item) => ({
                        _id: INCLUSION_STATE[item],
                        label: INCLUSION_STATE_NAME[item],
                      }))}
                      sx={{
                        width: "100%",
                        display: "flex",
                        "*": { display: "flex", justifyContent: "center" },
                      }}
                      renderInput={(params) => (
                        <CustomInput
                          label="Inclusion Progress*"
                          placeholder="Inclusion Progress*"
                          {...params}
                        />
                      )}
                    />

                    {fields.inclusion_state !== INCLUSION_STATE.I3 && (
                      <MobileDatePicker
                        value={moment(fields.date)}
                        inputFormat="DD/MM/YYYY"
                        onChange={(val) => {
                          setFields({
                            ...fields,
                            err: "",
                            date: val.toISOString(),
                          });
                        }}
                        label="Next Followup date*"
                        renderInput={(params) => (
                          <CustomInput
                            placeholder="Next Followup  date*"
                            {...params}
                          />
                        )}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {initialHearingStage >= 1
                      ? renderDisabledQuestion("hq1", hearingQuestionLabels[0])
                      : renderQuestion(
                          "hq1",
                          hearingQuestionLabels[0],
                          (checkedValue) =>
                            handleQuestionChange(
                              "hq1",
                              ["hq1", "hq2", "hq3", "hq4"],
                              "hearing_state",
                              [
                                HEARING_STATE.H1,
                                HEARING_STATE.H2,
                                HEARING_STATE.H3,
                                HEARING_STATE.H4,
                              ],
                              checkedValue,
                            ),
                        )}

                    {fields.hq1 &&
                      (initialHearingStage >= 2
                        ? renderDisabledQuestion(
                            "hq2",
                            hearingQuestionLabels[1],
                          )
                        : renderQuestion(
                            "hq2",
                            hearingQuestionLabels[1],
                            (checkedValue) =>
                              handleQuestionChange(
                                "hq2",
                                ["hq1", "hq2", "hq3", "hq4"],
                                "hearing_state",
                                [
                                  HEARING_STATE.H1,
                                  HEARING_STATE.H2,
                                  HEARING_STATE.H3,
                                  HEARING_STATE.H4,
                                ],
                                checkedValue,
                              ),
                          ))}

                    {fields.hq1 &&
                      fields.hq2 &&
                      (initialHearingStage >= 3
                        ? renderDisabledQuestion(
                            "hq3",
                            hearingQuestionLabels[2],
                          )
                        : renderQuestion(
                            "hq3",
                            hearingQuestionLabels[2],
                            (checkedValue) =>
                              handleQuestionChange(
                                "hq3",
                                ["hq1", "hq2", "hq3", "hq4"],
                                "hearing_state",
                                [
                                  HEARING_STATE.H1,
                                  HEARING_STATE.H2,
                                  HEARING_STATE.H3,
                                  HEARING_STATE.H4,
                                ],
                                checkedValue,
                              ),
                          ))}

                    {fields.hq1 &&
                      fields.hq2 &&
                      fields.hq3 &&
                      (initialHearingStage >= 4
                        ? renderDisabledQuestion(
                            "hq4",
                            hearingQuestionLabels[3],
                          )
                        : renderQuestion(
                            "hq4",
                            hearingQuestionLabels[3],
                            (checkedValue) =>
                              handleQuestionChange(
                                "hq4",
                                ["hq1", "hq2", "hq3", "hq4"],
                                "hearing_state",
                                [
                                  HEARING_STATE.H1,
                                  HEARING_STATE.H2,
                                  HEARING_STATE.H3,
                                  HEARING_STATE.H4,
                                ],
                                checkedValue,
                              ),
                          ))}

                    <CustomInput
                      disabled
                      value={findObjectKeyByValue(
                        fields.hearing_state,
                        HEARING_STATE,
                      )}
                      label="Hearing Progress*"
                    />

                    {initialSpeechStage >= 1
                      ? renderDisabledQuestion("sq1", speechQuestionLabels[0])
                      : renderQuestion(
                          "sq1",
                          speechQuestionLabels[0],
                          (checkedValue) =>
                            handleQuestionChange(
                              "sq1",
                              ["sq1", "sq2", "sq3", "sq4"],
                              "speech_state",
                              [
                                SPEECH_STATE.S1,
                                SPEECH_STATE.S2,
                                SPEECH_STATE.S3,
                                SPEECH_STATE.S4,
                              ],
                              checkedValue,
                            ),
                        )}

                    {fields.sq1 &&
                      (initialSpeechStage >= 2
                        ? renderDisabledQuestion("sq2", speechQuestionLabels[1])
                        : renderQuestion(
                            "sq2",
                            speechQuestionLabels[1],
                            (checkedValue) =>
                              handleQuestionChange(
                                "sq2",
                                ["sq1", "sq2", "sq3", "sq4"],
                                "speech_state",
                                [
                                  SPEECH_STATE.S1,
                                  SPEECH_STATE.S2,
                                  SPEECH_STATE.S3,
                                  SPEECH_STATE.S4,
                                ],
                                checkedValue,
                              ),
                          ))}

                    {fields.sq1 &&
                      fields.sq2 &&
                      (initialSpeechStage >= 3
                        ? renderDisabledQuestion("sq3", speechQuestionLabels[2])
                        : renderQuestion(
                            "sq3",
                            speechQuestionLabels[2],
                            (checkedValue) =>
                              handleQuestionChange(
                                "sq3",
                                ["sq1", "sq2", "sq3", "sq4"],
                                "speech_state",
                                [
                                  SPEECH_STATE.S1,
                                  SPEECH_STATE.S2,
                                  SPEECH_STATE.S3,
                                  SPEECH_STATE.S4,
                                ],
                                checkedValue,
                              ),
                          ))}

                    {fields.sq1 &&
                      fields.sq2 &&
                      fields.sq3 &&
                      (initialSpeechStage >= 4
                        ? renderDisabledQuestion("sq4", speechQuestionLabels[3])
                        : renderQuestion(
                            "sq4",
                            speechQuestionLabels[3],
                            (checkedValue) =>
                              handleQuestionChange(
                                "sq4",
                                ["sq1", "sq2", "sq3", "sq4"],
                                "speech_state",
                                [
                                  SPEECH_STATE.S1,
                                  SPEECH_STATE.S2,
                                  SPEECH_STATE.S3,
                                  SPEECH_STATE.S4,
                                ],
                                checkedValue,
                              ),
                          ))}

                    <CustomInput
                      disabled
                      value={findObjectKeyByValue(
                        fields.speech_state,
                        SPEECH_STATE,
                      )}
                      label="Speech Progress*"
                    />
                  </>
                )}

                <Grid container columnSpacing={2}>
                  <Grid item xs={4}>
                    <CustomInput
                      disabled={loading}
                      value={fields.daily_usage}
                      onChange={(e) =>
                        setFields({
                          ...fields,
                          err: "",
                          daily_usage: e.target.value,
                        })
                      }
                      min={0}
                      inputProps={{
                        min: 0,
                        max: 24,
                      }}
                      type="number"
                      label={"Average Daily Usage (H)*"}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      inputProps={{
                        min: 0,
                        max: 7,
                      }}
                      disabled={loading}
                      value={fields.weekly_usage}
                      onChange={(e) =>
                        setFields({
                          ...fields,
                          err: "",
                          weekly_usage: e.target.value,
                        })
                      }
                      type="number"
                      label={"Average Weekly Usage (D)*"}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      disabled
                      value={fields.weekly_usage * fields.daily_usage}
                      label={"Average Weekly Usage (H)*"}
                    />
                  </Grid>
                </Grid>

                {user.data.role === USER_ROLES.SIT_TEAM &&
                  (params.followup_no === 1 ||
                    params.followup_no === 6 ||
                    params.followup_no === 8 ||
                    params?.beneficiary_filters?.product ===
                      "664c37ab2d6e6c47e5877636" ||
                    params?.beneficiary_filters?.product ===
                      "6694b9e3cfb8a8bd29c8cc64") && (
                    <CustomInput
                      disabled={loading}
                      value={fields.videos}
                      onChange={(e) =>
                        setFields({
                          ...fields,
                          err: "",
                          videos: e.target.value,
                        })
                      }
                      type="text"
                      label={"Video Link"}
                    />
                  )}

                {/* {user.data.role == USER_ROLES.SIT_TEAM && params.beneficiary_filters.product == "65b0eec3d69fd021868ce9fc" && (
                  <CustomInput
                    disabled={loading}
                    value={fields.videos}
                    onChange={(e) =>
                      setFields({ ...fields, err: "", videos: e.target.value })
                    }
                    type="text"
                    label={"Video Link"}
                  />
                )} */}
              </>
            ) : (
              <>
                <MobileDatePicker
                  value={moment(fields.date)}
                  inputFormat="DD/MM/YYYY"
                  onChange={(val) => {
                    setFields({ ...fields, err: "", date: val.toISOString() });
                  }}
                  label="Reschedule date*"
                  renderInput={(params) => (
                    <CustomInput placeholder="Reschedule date*" {...params} />
                  )}
                />
              </>
            )}
            <CustomInput
              disabled={loading}
              value={fields.remarks}
              onChange={(e) =>
                setFields({ ...fields, err: "", remarks: e.target.value })
              }
              multiline
              rows={3}
              type="text"
              label={"Remarks*"}
            />
          </>
        )}
      </CustomDialog>
    </>
  );
};
export default memo(UpdateFollowupStatusUI);
